import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import firebase from 'firebase/compat/app';
import {UserInfo} from '../model/user-info';
import {FirebaseApi} from '../services/firebase-api.service';
import {Router} from '@angular/router';
import {AuthService} from '../services/auth.service';
import {UserSubscription} from '../model/user-subscription';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CommonUtil} from '../utils/common.util';
import {SubscriptionState} from '../model/subscription-state.enum';
import {SubscriptionPlan} from '../enums/subscription-plan.enum';
import {Logger} from '../utils/logger';
import FirebaseUser = firebase.User;
import {EventEmitter, HostBinding, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Language} from '../../../../projects/common/src/lib/enums/language.enum';
import {ProjectData} from '../model/project-data';
import {ProjectAccessLevel} from '../../canvas/enums/project-access-level.enum';
import {ERROR_SNACK_DURATION, SnackKind, openSnack} from '../utils/snack.util';
import {CookieConsentService} from '../consent/cookie-consent.service';

export abstract class Authentication extends Localization {

  user: FirebaseUser;
  userInfo: UserInfo;
  subscription: UserSubscription;
  onSubscriptionChanged = new EventEmitter<UserSubscription>()
  enterpriseUser: UserInfo;

  readonly auth = inject(AuthService);
  private readonly cookieConsent = inject(CookieConsentService);

  /**
   * The cookie settings dialog, from any "Cookie settings" link. Taking
   * consent back has to be as easy as giving it, so the way in is on every
   * main surface, not only on the banner.
   */
  openCookieSettings() {
    this.cookieConsent.openSettings();
  }
  readonly router = inject(Router);
  readonly firebaseApi = inject(FirebaseApi);
  readonly snackBar = inject(MatSnackBar);

  constructor() {
    super();
    this.user = this.auth.currentUser;
    this.userInfo = this.auth.currentUserInfo;
    // Every page and dialog in the app extends this class, and these three
    // subscriptions used to live forever: a destroyed component kept receiving
    // auth/subscription emissions (and kept its whole component instance
    // reachable) for the rest of the session. `takeUntilDestroyed` ties them to
    // the component that created them — it works here because the constructor
    // runs inside an injection context.
    this.auth.userInfoSubject.pipe(takeUntilDestroyed()).subscribe(userInfo => {
      this.user = this.auth.currentUser;
      this.userInfo = userInfo;
    });
    this.auth.subscriptionSubject.pipe(takeUntilDestroyed()).subscribe(subscription => {
      if(this.subscription && subscription && this.subscription.plan_id !== subscription.plan_id){
        this.onSubscriptionChanged.emit(subscription);
      }
      this.subscription = subscription;
    });
    this.auth.enterpriseSubject.pipe(takeUntilDestroyed()).subscribe(enterpriseUser => {
      this.enterpriseUser = enterpriseUser;
    });
  }

  setLanguage(newLang: Language) {
    return this.localization.setLanguage(newLang).then((done) => {
      if (done) {
        Logger.log('SET LANGUAGE', newLang);
        // Remembered on the account where there is one. A visitor without one
        // (an anonymous or view-only session) is refused the write; the
        // language has already changed here, so there is nothing to tell them.
        this.auth.updateUserInfo({
          language: newLang
        }).catch(reason => {
          Logger.error('Saving the language failed', reason);
        });
        // location.reload();
      }
    });
  }

  signOut() {
    this.router.navigateByUrl('/signout');
    // this.auth.signOut().then(async result => {
    //   if (result) {
    //     await this.router.navigateByUrl('/login');
    //     window.location.reload();
    //   }
    // }).catch(reason => {
    //   this.showSnackBar(this.getString('error_unknown'));
    // });
  }

  showSnackBar(message: string, duration: number = 4000, action: string = this.getString('dismiss'),
               kind: SnackKind = 'neutral') {
    // An embed sits on someone else's website, so the chatter ("20 geometries
    // loaded") stays out of it. Errors do not: an Open Guest visitor measures
    // in an embed, and a save that failed silently is lost work.
    if (CommonUtil.inIframe() && kind !== 'error') {
      return;
    }
    return openSnack(this.snackBar, message, action, duration, kind);
  }

  /** An error, drawn as one: status icon, longer on screen, announced at once. */
  showErrorSnackBar(message: string, duration: number = ERROR_SNACK_DURATION,
                    action: string = this.getString('dismiss')) {
    return this.showSnackBar(message, duration, action, 'error');
  }

  get isSubscriptionActive(): boolean {
    // The subscription doc arrives async (subject starts as null) — templates
    // evaluate this getter before it loads, so it must be null-safe.
    const sub = this.subscription;
    if (!sub) {
      return false;
    }
    let expiresOn = sub.expires_on
    if (!expiresOn || Number.isNaN(expiresOn) || isNaN(expiresOn)) {
      expiresOn = sub.period_end
    }
    return expiresOn > new Date().getTime()
      && sub.state !== SubscriptionState.PAST_DUE
      && sub.state !== SubscriptionState.EXPIRED
      && sub.state !== SubscriptionState.UNPAID
      && sub.state !== SubscriptionState.INCOMPLETE;
  }

  get userId(): string {
    return this.user ? this.user.uid : null;
  }

  get isSignedIn(): boolean {
    return this.firebaseApi.isSignedIn();
  }

  get isRealUser(): boolean {
    return this.firebaseApi.isRealUser();
  }

  get isEnterpriseUser(): boolean {
    return this.auth.isEnterpriseUser;
  }

  /**
   * Name of the organization the user belongs to. The owner reads their own
   * `enterpriseProfile`; members read `enterpriseBrand`, the copy stamped onto
   * them (they cannot read the owner's document). `company` is the legacy
   * fallback for organizations that never filled in a profile.
   */
  get organizationName(): string {
    const info = this.userInfo;
    if (!info) {
      return '';
    }
    return (info.enterpriseProfile?.name
      || info.enterpriseBrand?.name
      || info.company
      || '').trim();
  }

  get organizationLogo(): string {
    const info = this.userInfo;
    if (!info || this.logoFailed) {
      return '';
    }
    return (info.enterpriseProfile?.logoUrl || info.enterpriseBrand?.logoUrl || '').trim();
  }

  /** A broken logo URL falls back to the icon instead of a broken image. */
  protected logoFailed = false;

  onLogoError() {
    this.logoFailed = true;
  }

  get isSubscriptionManagedBy(): boolean {
    return this.subscription && this.subscription.managed_by && !!this.subscription.managed_by;
  }

  /** A partner: pays for and manages other accounts' plans (its Clients page). */
  get isPartner(): boolean {
    return !!this.userInfo?.partner?.active;
  }

  /**
   * A partner now or before: one that staff stopped still manages, and can
   * stop, the plans it pays for, so its Clients page stays.
   */
  get hasClientsPage(): boolean {
    return !!this.userInfo?.partner?.since;
  }

  get hasEnterpriseSubscription(): boolean {
    return this.auth.hasEnterpriseSubscription;
  }

  get hasOpenGuestSubscription(): boolean {
    return this.auth.hasOpenGuestSubscription;
  }

  get hasProSubscription(): boolean {
    return this.auth.hasProSubscription;
  }

  get hasDeveloperSubscription(): boolean {
    return this.auth.hasDeveloperSubscription;
  }

  get hasEnterpriseUsers(): boolean {
    return this.getEnterpriseActiveUsersCount > 0;
  }

  get hasReachedMaxEnterpriseUsers(): boolean {
    return this.auth.hasReachedMaxEnterpriseUsers;
  }
  get isAllowedToCreateEnterpriseUser(): boolean {
    return this.auth.isAllowedToCreateEnterpriseUser;
  }

  get getEnterpriseActiveUsersCount() {
    return this.auth.getEnterpriseActiveUsersCount;
  }

  hasActiveEnterprisePlan(): boolean {
    return this.auth.hasActiveEnterprisePlan();
  }

  hasStandardPlan(): boolean {
    return false; // TODO IMPORTANT
  }

  get isGod(): boolean {
    return this.auth.isGod();
  }

  get isTrialing(): boolean{
    const sub = this.subscription;
    if (!sub) {
      return false;
    }
    let expiresOn = sub.expires_on
    if (!expiresOn || Number.isNaN(expiresOn) || isNaN(expiresOn)) {
      expiresOn = sub.period_end
    }
    return expiresOn > new Date().getTime()
      && sub.plan_id === SubscriptionPlan.TRIAL;
  }

  changePlan() {
    switch (this.subscription.plan_id) {
      case SubscriptionPlan.ENTERPRISE_MONTHLY:
      case SubscriptionPlan.ENTERPRISE_YEARLY:
        // A full organisation needs seats. This switched Yearly to Monthly
        // -- a downgrade -- under a button that said "Upgrade".
        return this.router.navigateByUrl('/dashboard/subscription/seats');
      case SubscriptionPlan.PRO_MONTHLY:
        return this.goToBilling(SubscriptionPlan.PRO_YEARLY, false);
    }
    return this.goToBilling(SubscriptionPlan.PRO_MONTHLY, false);
  }

  /** The saved cards are a section of Subscription. */
  updatePaymentMethod() {
    return this.router.navigateByUrl('/dashboard/subscription/payment-methods');
  }

  goToBilling(planId: SubscriptionPlan, updatePaymentMethod: boolean) {
    return this.auth.createCustomToken().then(result => {
      Logger.log(result);
      const loginToken = result.token;
      // The custom token is handed over in sessionStorage (same origin, same
      // tab) instead of the query string, which lands in browser history,
      // referrers and server logs.
      CommonUtil.stashBillingToken(loginToken);
      let url = `billing?pid=${planId}`;
      if (updatePaymentMethod) {
        url += '&upm=true';
      }
      this.router.navigateByUrl(url);
    }).catch(reason => {
      Logger.error(reason);
      // It used to stop at the log: "Choose a plan" flickered and nothing
      // happened, with nothing to tell the user why.
      this.showErrorSnackBar(this.getString('billing_open_failed'));
    });
  }
}
